import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import {
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { FloatingLayer } from '../../FloatingManager';
import { floatingComponentStyles } from '../../../styles/shareableStyles';
import { SingleSelectLabel } from '../../Dropdown/SingleSelect';
import InlineMultiSelectOptions from './InlineMultiSelectOptions';
import { IInlineMultiSelectMenuOption } from './types';

interface IProps<ValueType> {
  options: IInlineMultiSelectMenuOption<ValueType>[];
  selectedKeys: ValueType[];
  onSubmit: (selected: ValueType[]) => void;
  label: ReactNode;
  onMenuOpened?: () => void;
  labelIsAlwaysActive?: boolean;
}

const MenuWrapper = styled.div`
  width: 160px;
  ${floatingComponentStyles};
`;

const InlineMultiSelectMenu = <ValueType,>({
  options,
  selectedKeys,
  onSubmit,
  label,
  onMenuOpened,
  labelIsAlwaysActive,
}: IProps<ValueType>) => {
  const [isOpen, setIsOpen] = useState(false);

  const { floatingStyles, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (open && onMenuOpened) {
        onMenuOpened();
      }
    },
    placement: 'bottom-start',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    ancestorScroll: true,
  });
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const handleSubmit = (selection: ValueType[]) => {
    onSubmit(selection);
    setIsOpen(false);
  };

  return (
    <>
      <SingleSelectLabel
        ref={refs.setReference}
        {...getReferenceProps()}
        isInteractive={true}
        isAlwaysActive={labelIsAlwaysActive}
      >
        {label}
      </SingleSelectLabel>
      {isOpen && (
        <FloatingLayer>
          <MenuWrapper
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <InlineMultiSelectOptions
              onSubmit={handleSubmit}
              options={options}
              selectedKeys={selectedKeys}
            />
          </MenuWrapper>
        </FloatingLayer>
      )}
    </>
  );
};

export default InlineMultiSelectMenu;
